import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import NotificationBadge from '../../components/NotificationBadge';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { format, subDays } from 'date-fns';
import UsageMetricCard from './components/UsageMetricCard';
import UserManagementPanel from './components/UserManagementPanel';
import SystemHealthMonitor from './components/SystemHealthMonitor';
import RevenueMetricsPanel from './components/RevenueMetricsPanel';
import PlatformActivityChart from './components/PlatformActivityChart';
import AlertsPanel from './components/AlertsPanel';
import ReferralRewardsPanel from './components/ReferralRewardsPanel';
import ReferralPerformanceMetrics from './components/ReferralPerformanceMetrics';
import { platformEventsService } from '../../services/platformEventsService';
import BrandMonitoringPanel from './components/BrandMonitoringPanel';
import MaintenanceModeControl from './components/MaintenanceModeControl';
import ReEngagementEmailPanel from './components/ReEngagementEmailPanel';


import MasterReferralLinkCard from './components/MasterReferralLinkCard';
import ConnectionStatusBadge from './components/ConnectionStatusBadge';
import DataFreshnessIndicator from './components/DataFreshnessIndicator';
import {
  MetricCardSkeleton,
  ChartPanelSkeleton,
  SystemHealthSkeleton,
  AlertsPanelSkeleton,
  RevenuePanelSkeleton,
  ReferralCardSkeleton,
  UserManagementSkeleton
} from './components/SkeletonLoader';


const MyBodyTodayOfficersMasterDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [usageMetrics, setUsageMetrics] = useState({
    totalUsers: 0,
    totalUsersTrend: 0,
    dailyActiveUsers: 0,
    dauTrend: 0,
    premiumUsers: 0,
    premiumTrend: 0,
    conversionRate: 0,
    conversionTrend: 0
  });
  const [revenueMetrics, setRevenueMetrics] = useState({
    monthlyRevenue: 0,
    revenueTrend: 0,
    averageRevenue: 0,
    lifetimeValue: 0
  });
  const [systemHealth, setSystemHealth] = useState({
    status: 'healthy',
    uptime: 99.9,
    responseTime: 120,
    errorRate: 0.1
  });
  const [activityData, setActivityData] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [error, setError] = useState('');
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [showPerformanceMetrics, setShowPerformanceMetrics] = useState(false);
  const [referralData, setReferralData] = useState({
    referralCode: '',
    referralLink: ''
  });
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [brandMonitoringData, setBrandMonitoringData] = useState(null);

  // Check officer access - recalculate when userProfile or user changes
  const hasAccess = useMemo(() => {
    return hasFeatureAccess(userProfile, OFFICER_FEATURES?.MASTER_DASHBOARD, user);
  }, [userProfile, user]);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  useEffect(() => {
    loadDashboardData();
    loadReferralData();
  }, [dateRange]);

  // Real-time events subscription
  useEffect(() => {
    if (!hasAccess || !userProfile) return;

    // Subscribe to platform events
    const channel = platformEventsService?.subscribeToEvents(
      (event) => {
        setRealtimeEvents(prev => [event, ...prev]?.slice(0, 50)); // Keep last 50 events
        
        // Update unacknowledged count
        if (!event?.acknowledged) {
          setUnacknowledgedCount(prev => prev + 1);
        }

        // Show browser notification for critical events
        if (event?.eventSeverity === 'critical') {
          showBrowserNotification(event);
        }
      },
      {
        // Subscribe to all event types
        eventTypes: ['USER_SIGNUP', 'PREMIUM_CONVERSION', 'PAYMENT_FAILURE', 'SYSTEM_ANOMALY', 'FEATURE_SPIKE']
      }
    );

    setRealtimeChannel(channel);

    // Load initial unacknowledged count
    loadUnacknowledgedCount();

    // Cleanup on unmount
    return () => {
      if (channel) {
        platformEventsService?.unsubscribe(channel);
      }
    };
  }, [hasAccess, userProfile]);

  const loadUnacknowledgedCount = async () => {
    try {
      const { data, error } = await platformEventsService?.getUnacknowledgedCount();
      if (!error && data !== null) {
        setUnacknowledgedCount(data);
      }
    } catch (err) {
      logger?.error('Failed to load unacknowledged count', err);
    }
  };

  const showBrowserNotification = (event) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Critical Platform Event', {
        body: `${event?.eventCategory}: ${event?.eventType}`,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
  };

  const handleNotificationClick = () => {
    navigate('/critical-alerts-dashboard');
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      await Promise.all([
        loadUsageMetrics(),
        loadRevenueMetrics(),
        loadSystemHealth(),
        loadActivityData(),
        loadCriticalAlerts(),
        loadPaymentFailures(),
        loadSubscriptionEvents(),
        loadChurnRiskData()
      ]);
    } catch (err) {
      setError('Failed to load dashboard data');
      logger?.error('Master dashboard load error', err);
    } finally {
      // CRITICAL: Always set loading to false, even if there are errors
      // This prevents the dashboard from being stuck in loading/graying state
      setLoading(false);
    }
  };

  const loadUsageMetrics = async () => {
    try {
      // Check if supabase is initialized
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get total users count
      const { count: totalUsers, error: totalUsersError } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      if (totalUsersError) {
        console.error('Failed to load total users:', totalUsersError);
        throw totalUsersError;
      }

      // Get recent activity (last 24 hours)
      const yesterday = subDays(new Date(), 1)?.toISOString();
      const { data: recentActivity, error: activityError } = await supabase?.from('symptom_entries')?.select('user_id')?.gte('created_at', yesterday);

      if (activityError) {
        console.error('Failed to load recent activity:', activityError);
        throw activityError;
      }

      const uniqueActiveUsers = new Set(recentActivity?.map(a => a.user_id));
      const dailyActiveUsers = uniqueActiveUsers?.size;

      // FIXED: Get premium users using two-step query to avoid RLS foreign key join issues
      // Step 1: Get plan IDs for premium plans (no RLS on subscription_plans table)
      const { data: premiumPlans, error: plansError } = await supabase
        ?.from('subscription_plans')
        ?.select('id')
        ?.in('plan_type', ['premium', 'family', 'basic_ultra']);

      if (plansError) {
        console.error('Failed to load premium plans:', plansError);
        throw plansError;
      }

      const premiumPlanIds = premiumPlans?.map(p => p?.id) || [];

      // Step 2: Count active subscriptions with premium plan IDs
      const { count: premiumUsers, error: premiumError } = await supabase
        ?.from('user_subscriptions')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('status', 'active')
        ?.in('plan_id', premiumPlanIds);

      if (premiumError) {
        console.error('Failed to load premium users:', premiumError);
        throw premiumError;
      }

      const conversionRate = totalUsers > 0 ? ((premiumUsers / totalUsers) * 100)?.toFixed(1) : 0;

      console.log('Usage Metrics loaded:', {
        totalUsers,
        dailyActiveUsers,
        premiumUsers,
        conversionRate,
        premiumPlanIds: premiumPlanIds?.length
      });

      setUsageMetrics({
        totalUsers: totalUsers || 0,
        totalUsersTrend: 0,
        dailyActiveUsers,
        dauTrend: 0,
        premiumUsers,
        premiumTrend: 0,
        conversionRate: parseFloat(conversionRate),
        conversionTrend: 0
      });
    } catch (err) {
      console.error('Failed to load usage metrics:', err);
      logger?.error('Failed to load usage metrics', err);
      // Set zeros on error
      setUsageMetrics({
        totalUsers: 0,
        totalUsersTrend: 0,
        dailyActiveUsers: 0,
        dauTrend: 0,
        premiumUsers: 0,
        premiumTrend: 0,
        conversionRate: 0,
        conversionTrend: 0
      });
    }
  };

  const loadRevenueMetrics = async () => {
    try {
      // SIMPLIFIED: Remove all revenue queries
      setRevenueMetrics({
        monthlyRevenue: 0,
        revenueTrend: 0,
        averageRevenue: 0,
        lifetimeValue: 0
      });
    } catch (error) {
      logger?.error('Failed to load revenue metrics', error);
      setRevenueMetrics({
        monthlyRevenue: 0,
        revenueTrend: 0,
        averageRevenue: 0,
        lifetimeValue: 0
      });
    }
  };

  const loadSystemHealth = async () => {
    try {
      // SIMPLIFIED: Remove system health checks
      setSystemHealth({
        status: 'healthy',
        uptime: 99.9,
        responseTime: 120,
        errorRate: 0.1
      });
    } catch (error) {
      logger?.error('Failed to load system health', error);
      setSystemHealth({
        status: 'error',
        uptime: 99.5,
        responseTime: 250,
        errorRate: 2.5
      });
    }
  };

  const loadActivityData = async () => {
    try {
      // SIMPLIFIED: Remove activity chart data
      setActivityData([]);
    } catch (error) {
      logger?.error('Failed to load activity data', error);
      setActivityData([]);
    }
  };

  const loadCriticalAlerts = async () => {
    try {
      // SIMPLIFIED: Remove critical alerts
      setCriticalAlerts([]);
    } catch (error) {
      logger?.error('Failed to load critical alerts', error);
      setCriticalAlerts([]);
    }
  };

  const loadPaymentFailures = async () => {
    try {
      // SIMPLIFIED: Remove payment failures
    } catch (error) {
      logger?.error('Failed to load payment failures', error);
    }
  };

  const loadSubscriptionEvents = async () => {
    try {
      // SIMPLIFIED: Remove subscription events
    } catch (error) {
      logger?.error('Failed to load subscription events', error);
    }
  };

  const loadChurnRiskData = async () => {
    try {
      // Get churn risk data
      const { data } = await supabase?.from('churn_risk')?.select('*');
      // Process data as needed
    } catch (error) {
      logger?.error('Failed to load churn risk data', error);
    }
  };

  const loadReferralData = () => {
    try {
      // Use custom referral code if available, otherwise generate from user ID
      const referralCode = userProfile?.custom_referral_code || `MBT${user?.id?.slice(0, 8)?.toUpperCase()}`;
      const referralLink = `https://mybodytoday1.com/sign-up?ref=${referralCode}`;

      setReferralData({
        referralCode,
        referralLink
      });
    } catch (error) {
      logger?.error('Failed to generate referral data', error);
    }
  };

  const loadRealtimeMetrics = async () => {
    // Update real-time metrics without showing loading state
    try {
      await Promise.all([
        loadUsageMetrics(),
        loadSystemHealth()
      ]);
    } catch (error) {
      logger?.error('Failed to update real-time metrics', error);
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleExportData = async () => {
    try {
      const exportData = {
        generatedAt: new Date()?.toISOString(),
        dateRange,
        usageMetrics,
        revenueMetrics,
        systemHealth,
        activityData
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `master-dashboard-export-${format(new Date(), 'yyyy-MM-dd')}.json`;
      link?.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger?.error('Failed to export data', error);
      setError('Failed to export dashboard data');
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await loadDashboardData();
      await loadReferralData();
      setLastRefreshTime(new Date());
    } catch (err) {
      logger?.error('Manual refresh failed', err);
      setError('Failed to refresh dashboard data');
    } finally {
      setRefreshing(false);
    }
  };

  if (!hasAccess && !loading) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Master Dashboard - MyBodyToday Officers</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-20">
        <UserProfileHeader />

        {/* Header with Notification Badge */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-2.5 py-3 sm:px-3 sm:py-4 md:px-6 md:py-5">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-base sm:text-lg md:text-xl font-bold mb-1">Master Dashboard</h1>
                <p className="text-xs sm:text-sm md:text-base text-indigo-100">Executive Overview & Platform Monitoring</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/admin-error-monitoring')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm shadow-lg"
                  title="View Admin Error Monitoring Dashboard"
                >
                  <Icon name="AlertTriangle" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden md:inline">Error Monitor</span>
                </button>
                <NotificationBadge
                  count={unacknowledgedCount}
                  severity={unacknowledgedCount > 10 ? 'critical' : unacknowledgedCount > 5 ? 'warning' : 'info'}
                  onClick={handleNotificationClick}
                />
              </div>
            </div>
            
            {/* Connection Status and Data Freshness Row */}
            <div className="flex flex-wrap items-center gap-3">
              <ConnectionStatusBadge />
              <DataFreshnessIndicator 
                lastRefreshTime={lastRefreshTime}
                onRefresh={handleManualRefresh}
                isRefreshing={refreshing}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} strokeWidth={2} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error Loading Dashboard</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <>
              {/* Referral Link Skeleton */}
              <ReferralCardSkeleton />

              {/* Usage Metrics Grid Skeletons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCardSkeleton />
                <MetricCardSkeleton />
                <MetricCardSkeleton />
                <MetricCardSkeleton />
              </div>

              {/* System Health Skeleton */}
              <SystemHealthSkeleton />

              {/* Platform Activity Chart Skeleton */}
              <ChartPanelSkeleton />

              {/* Revenue Metrics Skeleton */}
              <RevenuePanelSkeleton />

              {/* Referral Rewards Panel Skeleton */}
              <ChartPanelSkeleton />

              {/* Critical Alerts Panel Skeleton */}
              <AlertsPanelSkeleton />

              {/* User Management Panel Skeleton */}
              <UserManagementSkeleton />
            </>
          ) : (
            <>
              {/* Maintenance Mode Control - Prominent at top */}
              <div className="border-2 border-amber-400/60 rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 flex items-center gap-2">
                  <span className="text-white text-base font-bold tracking-wide">⚙️ Maintenance Mode Control</span>
                  <span className="ml-auto text-amber-100 text-xs font-medium opacity-80">Officer Access Only</span>
                </div>
                <MaintenanceModeControl />
              </div>

              {/* Master Referral Link Section */}
              <MasterReferralLinkCard 
                referralCode={referralData?.referralCode}
                referralLink={referralData?.referralLink}
              />

              {/* Usage Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <UsageMetricCard
                  title="Total Users"
                  value={usageMetrics?.totalUsers}
                  trend={usageMetrics?.totalUsersTrend}
                  icon="Users"
                  color="blue"
                />
                <UsageMetricCard
                  title="Daily Active Users"
                  value={usageMetrics?.dailyActiveUsers}
                  trend={usageMetrics?.dauTrend}
                  icon="Activity"
                  color="green"
                />
                <UsageMetricCard
                  title="Premium Users"
                  value={usageMetrics?.premiumUsers}
                  trend={usageMetrics?.premiumTrend}
                  icon="Star"
                  color="purple"
                />
                <UsageMetricCard
                  title="Conversion Rate"
                  value={`${usageMetrics?.conversionRate}%`}
                  trend={usageMetrics?.conversionTrend}
                  icon="TrendingUp"
                  color="orange"
                />
              </div>

              {/* System Health Monitor */}
              <SystemHealthMonitor systemHealth={systemHealth} />

              {/* Platform Activity Chart */}
              <PlatformActivityChart activityData={activityData} />

              {/* Revenue Metrics */}
              <div className="mb-6">
                <RevenueMetricsPanel revenueMetrics={revenueMetrics} />
              </div>

              {/* Referral Rewards Panel */}
              <ReferralRewardsPanel onViewDetails={() => setShowPerformanceMetrics(true)} />

              {/* Critical Alerts Panel */}
              <AlertsPanel alerts={criticalAlerts} realtimeEvents={realtimeEvents} />

              {/* User Management Panel */}
              <UserManagementPanel />

              {/* Re-Engagement Email Campaign */}
              <ReEngagementEmailPanel />

              {/* Brand Monitoring Panel */}
              <BrandMonitoringPanel />
            </>
          )}
        </div>

        <BottomTabNavigation />
        
        {/* Referral Performance Metrics Modal */}
        {showPerformanceMetrics && (
          <ReferralPerformanceMetrics onClose={() => setShowPerformanceMetrics(false)} />
        )}
      </div>
    </>
  );
};

export default MyBodyTodayOfficersMasterDashboard;